import React, { useCallback, useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { TodayIs } from 'components/utils/TodayIs';
import ImportScript from 'components/utils/ImportScript';
import Footer from 'pages/main/Footer';
import Alerts from 'pages/alerts/Alerts';
import Messages from 'pages/messages/Messages';
import Person from 'pages/main/Person';
import ShippingDecaViewer from './ShippingDecaViewer';
import 'css/customDialog.css';
import 'css/shippingResponsive.css';
import 'css/shippingEmailResponsive.css';
import 'css/shippingDecaResponsive.css';
import { ENDPOINT } from 'constants/variables';
import { warningErrorAction } from 'actions/ErrorActions';

const HomeShippingDeCA = ({ typeList }) => {
  const { t, i18n } = useTranslation(['listDataShipping']);
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const token = useSelector((state) => state.loginUser.token);
  const enabled = useSelector((state) => state.loginUser.enabled);
  const roles = useSelector((state) => state.loginUser.roles);
  const defaultStore = useSelector((state) => state.loginUser.defaultStore);
  const [shippingNumber, setShippingNumber] = useState('');
  const [decaServiceId, setDecaServiceId] = useState(null);
  const [downloadingHistory, setDownloadingHistory] = useState(false);
  const scriptUrl = '/js/sb-admin-2.min.js';

  const listRoute = typeList === '1'
    ? '/shipping'
    : typeList === '2'
      ? '/shippingended'
      : '/shippingbegin';
  const currentRoute = typeList === '1'
    ? `/shippingdeca/${id}`
    : typeList === '2'
      ? `/shippingdecaended/${id}`
      : `/shippingdecabegin/${id}`;

  const changeSelectedLanguage = () => {
    i18n.language === 'es' ? i18n.changeLanguage('en') : i18n.changeLanguage('es');
  };

  const handleShippingLoaded = useCallback((shipping) => {
    setShippingNumber(shipping?.ownshipping_id ?? shipping?.number_shipping ?? '');
  }, []);

  const handleServiceLoaded = useCallback((loadedService) => {
    setDecaServiceId(loadedService?.id || null);
  }, []);

  const downloadHistory = async () => {
    if (!decaServiceId || downloadingHistory) return;
    setDownloadingHistory(true);
    try {
      const response = await fetch(
        `${ENDPOINT}/deca/documents/service/${decaServiceId}/history/pdf`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error('history-download');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Historial_DeCA_Carta_${shippingNumber || id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      dispatch(warningErrorAction('No se pudo descargar el historial completo del DeCA'));
    } finally {
      setDownloadingHistory(false);
    }
  };

  if (enabled === false) return <Navigate to="/home" />;

  return (
    <>
      <ImportScript resourceUrl={scriptUrl} />
      <ToastContainer />
      <div id="page-top">
        <div id="wrapper" className="shipping-page shipping-deca-page">
          <ul className="navbar-nav bg-gradient-primary toggled sidebar sidebar-dark accordion" id="accordionSidebar">
            <Link className="sidebar-brand d-flex align-items-center justify-content-center" to="/home">
              <div className="sidebar-brand-icon rotate-n-15"><DashboardIcon /></div>
              <div className="sidebar-brand-text mx-3">{t('Text_Title')}</div>
            </Link>
            <hr className="sidebar-divider my-0" />
            <li className="nav-item active">
              <div className="nav-link">
                <i className="fas fa-fw fa-tachometer-alt" />
                <span>{t('Menu_Main')}</span>
              </div>
            </li>
            <hr className="sidebar-divider" />
            {roles[0].listmenus.find((key) => key.name === 'Menu_Settings') && (
              <li className="nav-item">
                <Link className="nav-link collapsed" to={currentRoute} data-toggle="collapse"
                  data-target="#collapseAdmin" aria-expanded="true" aria-controls="collapseAdmin">
                  <SettingsIcon />
                  <span>{t('Menu_Settings')}</span>
                </Link>
                <div id="collapseAdmin" className="collapse" aria-labelledby="headingAdmin"
                  data-parent="#accordionSidebar">
                  <div className="bg-white py-2 collapse-inner rounded">
                    <h6 className="collapse-header">{t('Custom_Menu_Settings')}</h6>
                    <Link className="collapse-item" to={currentRoute} onClick={changeSelectedLanguage}>
                      {i18n.language === 'es'
                        ? t('SubMenu_Settings_ChangeToEnglish')
                        : t('SubMenu_Settings_ChangeToSpanish')}
                    </Link>
                  </div>
                </div>
              </li>
            )}
            <li className="nav-item">
              <Link className="nav-link" to={listRoute} onClick={() => navigate(listRoute)}>
                <LogoutIcon />
                <span>{t('Button_Back')}</span>
              </Link>
            </li>
            <hr className="sidebar-divider d-none d-md-block" />
            <div className="text-center d-none d-md-inline">
              <button className="rounded-circle border-0" id="sidebarToggle" />
            </div>
          </ul>

          <div id="content-wrapper" className="d-flex flex-column">
            <div id="content">
              <nav className="navbar navbar-expand navbar-light bg-white topbar mb-4 static-top shadow">
                <button id="sidebarToggleTop" className="btn btn-link d-md-none rounded-circle mr-3">
                  <i className="fa fa-bars" />
                </button>
                <ul className="navbar-nav ml-auto">
                  <Person />
                  <div className="topbar-divider d-none d-sm-block" />
                  <div className="driver-name-ok shipping-header-company" title={defaultStore.tradename || t('Text_StoreEmpty')}>
                    <br />
                    {t('Text_StoreSelected')}&nbsp;
                    {defaultStore.tradename === null ? t('Text_StoreEmpty') : defaultStore.tradename}
                  </div>
                  <div className="topbar-divider d-none d-sm-block" />
                  <Alerts />
                  <Messages />
                  <div className="topbar-divider d-none d-sm-block" />
                  <li className="nav-item dropdown no-arrow mx-1 shipping-header-date">
                    <br />
                    <span className="mr-2 d-none d-lg-inline text-gray-600 small"><TodayIs /></span>
                  </li>
                </ul>
              </nav>
              <div className="container-fluid email-send-wrapper shipping-email-form shipping-deca-content">
                <div className="shipping-deca-layout">
                  <div className="email-card-header">
                    <div className="email-header-content">
                      <div className="email-header-icon" style={{ color: '#ff8a00' }}>
                        <i className="fas fa-qrcode" />
                      </div>
                      <div className="email-header-text" style={{ flex: 1 }}>
                        <div className="email-header-title2">
                          DeCA CARTA DE PORTES: {shippingNumber || '…'}
                        </div>
                      </div>
                      <div className="email-card-actions">
                        <button
                          type="button"
                          className="email-cancel-btn"
                          onClick={downloadHistory}
                          disabled={!decaServiceId || downloadingHistory}
                          style={{ minWidth: 0 }}
                        >
                          {downloadingHistory ? (
                            <><CircularProgress size={19} sx={{ color: '#4e73df', marginRight: '10px' }} /> Descargando…</>
                          ) : 'Descargar todo el historial'}
                        </button>
                        <button type="button" className="email-cancel-btn" onClick={() => navigate(listRoute)}>
                          Retroceder
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="deca-view-card">
                    <ShippingDecaViewer
                      id={id}
                      onShippingLoaded={handleShippingLoaded}
                      onServiceLoaded={handleServiceLoaded}
                    />
                  </div>
                </div>
              </div>
            </div>
            <Footer />
          </div>
        </div>
      </div>
    </>
  );
};

export default HomeShippingDeCA;
